import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {Form, Input} from 'antd';
import {StyledAmountGhostInput} from './StyledAmountGhostInput';
import {textHelpers} from '../../utils/helpers';

const AmountGhostInput = ({
  displayedValue,
  inputProps = {},
  formItemProps = {},
  form,
  name = 'amount',
  ...rest
}) => {

  const amountInputRef = useRef(null);
  const amountGhostTextRef = useRef(null);

  const amount = Form.useWatch(name, form);

  // get form instance from context (hook must be called unconditionally)
  const formInstanceFromContext = Form.useFormInstance();
  const formInstance = form || formInstanceFromContext;

  useEffect(() => {
    if (amountGhostTextRef.current && amountInputRef.current) {
      amountInputRef.current.input.style.width = `${(amountGhostTextRef?.current?.offsetWidth || 0) + 4}px`;
    }
  }, [amount]);

  const displayAmount = (amount !== undefined && amount !== null && amount !== '') ? textHelpers.roundToTwo(amount) : ' ';

  const mergedInputProps = {
    ...inputProps,
    onBeforeInput: (e) => {
      try {
        if (typeof inputProps.onBeforeInput === 'function') inputProps.onBeforeInput(e);
        const data = e.data;
        if (!data) return;
        // block minus sign
        if (data === '-' || data === '−') {
          e.preventDefault();
          return;
        }

        const input = e.target;
        if (!input) return;
        const value = input.value || '';
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;

        // build prospective value if this input data is inserted
        const prospective = value.slice(0, selectionStart) + data + value.slice(selectionEnd);
        const normalized = String(prospective).replace(/,/g, '.');
        // ensure non-negative
        const numericProspective = Number(normalized);
        if (Number.isFinite(numericProspective) && numericProspective < 0) {
          e.preventDefault();
          return;
        }

        const decIndex = normalized.indexOf('.');
        if (decIndex === -1) return; // no decimal part yet
        const fraction = normalized.slice(decIndex + 1);
        if (fraction.length > 2) {
          e.preventDefault();
        }
      } catch (err) {
        // ignore
      }
    },
    onBlur: (e) => {
      // round value on blur and set back to form before calling external onBlur
      try {
        const val = e?.target?.value;
        if (val !== undefined && formInstance) {
          // normalize comma to dot to support locales
          const normalized = String(val).replace(/,/g, '.');
          let rounded = textHelpers.roundToTwo(normalized);
          // clamp to min 0
          const numericRounded = Number(rounded);
          const finalValue = (Number.isFinite(numericRounded) && numericRounded < 0) ? 0 : numericRounded;
          formInstance.setFieldsValue({ [name]: finalValue });
        }
      } catch (err) {
        // ignore
      }

      // preserve existing onBlur if provided (call after rounding so submit gets rounded value)
      if (typeof inputProps.onBlur === 'function') inputProps.onBlur(e);
    },
    onInput: (e) => {
      try {
        if (typeof inputProps.onInput === 'function') inputProps.onInput(e);
        const input = e.target;
        if (!input) return;
        const raw = input.value || '';
        // normalize commas to dot
        let normalized = String(raw).replace(/,/g, '.');
        // remove whitespace
        normalized = normalized.trim();
        // prevent negative sign anywhere
        if (normalized.indexOf('-') !== -1) {
          normalized = normalized.replace(/-/g, '');
        }

        // handle leading dot: allow '.5' -> '0.5'
        if (normalized.startsWith('.')) normalized = '0' + normalized;

        const decIndex = normalized.indexOf('.');
        if (decIndex === -1) {
          // no decimal point, just update form with integer part (or empty)
          if (formInstance && name) {
            const numericInt = normalized === '' ? undefined : Number(normalized);
            if (numericInt === undefined || Number.isFinite(numericInt)) {
              formInstance.setFieldsValue({ [name]: numericInt });
              if (typeof inputProps.onChange === 'function') inputProps.onChange(numericInt);
            }
          }
          return;
        }

        // has decimal part - truncate to max 2 digits
        const integerPart = normalized.slice(0, decIndex);
        let fraction = normalized.slice(decIndex + 1);
        if (fraction.length > 2) fraction = fraction.slice(0, 2);
        const newValStr = integerPart + '.' + fraction;

        // update DOM value if changed
        if (newValStr !== normalized) {
          input.value = newValStr;
        }

        const numeric = newValStr === '' ? undefined : Number(newValStr);
        if (formInstance && name) {
          formInstance.setFieldsValue({ [name]: numeric });
          if (typeof inputProps.onChange === 'function') inputProps.onChange(numeric);
        } else {
          // dispatch input event so controlled/form handlers update
          const ev = new Event('input', { bubbles: true });
          input.dispatchEvent(ev);
        }
      } catch (err) {
        // ignore
      }
    },
    onKeyDown: (e) => {
      try {
        if (typeof inputProps.onKeyDown === 'function') inputProps.onKeyDown(e);
        // only handle digit keys (0-9) and numpad digits
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const key = e.key;
        // don't allow minus sign
        if (key === '-' || key === '−') {
          e.preventDefault();
          return;
        }
        // handle decimal separator: allow only one
        if (key === '.' || key === ',') {
          const inputForSep = e.target;
          const v = inputForSep.value || '';
          if (v.indexOf('.') !== -1 || v.indexOf(',') !== -1) {
            e.preventDefault();
          }
          return;
        }
        if (!/^[0-9]$/.test(key)) return;

        const input = e.target;
        const value = input.value || '';
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;

        // find decimal separator (dot or comma)
        const dotIndex = value.indexOf('.');
        const commaIndex = value.indexOf(',');
        const decIndex = dotIndex !== -1 ? dotIndex : (commaIndex !== -1 ? commaIndex : -1);
        if (decIndex === -1) return; // no decimal part yet

        // if caret is before or on decimal separator, allow
        if (selectionStart <= decIndex) return;

        // compute how many fractional digits will remain after this keypress considering selection
        const fractionalStart = decIndex + 1;
        const currentFractionDigits = value.length - fractionalStart;
        // count selected fraction digits that will be replaced by this key
        const selectedFractionDigits = Math.max(0, Math.min(selectionEnd, value.length) - Math.max(selectionStart, fractionalStart));
        const fractionDigitsAfterKey = currentFractionDigits - selectedFractionDigits + 1; // +1 for the new digit


        if (fractionDigitsAfterKey > 2) {
          e.preventDefault();
        }
      } catch (err) {
        // ignore
      }
    },
    onPaste: (e) => {
      try {
        if (typeof inputProps.onPaste === 'function') inputProps.onPaste(e);
        const pasted = (e.clipboardData || window.clipboardData).getData('text');
        if (!pasted) return;
        const input = e.target;
        const value = input.value || '';
        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;

        // build prospective new value
        const newValue = value.slice(0, selectionStart) + pasted + value.slice(selectionEnd);
        const normalized = String(newValue).replace(/,/g, '.');
        // clamp negative pasted values
        let numericNew = Number(normalized);
        if (!Number.isFinite(numericNew)) return;
        if (numericNew < 0) numericNew = 0;
        const decIndex = normalized.indexOf('.');
        if (decIndex === -1) return; // no decimal, allow paste

        const fraction = normalized.slice(decIndex + 1);
        if (fraction.length <= 2) return; // allowed

        // otherwise prevent default and insert truncated value with max 2 decimals
        e.preventDefault();
        const truncatedStr = normalized.slice(0, decIndex + 1 + 2);
        const numeric = Number(truncatedStr);
        // update DOM and form so value is consistent
        input.value = truncatedStr;
        if (formInstance && name) {
          const finalNum = (Number.isFinite(numeric) && numeric < 0) ? 0 : numeric;
          formInstance.setFieldsValue({ [name]: finalNum });
          if (typeof inputProps.onChange === 'function') inputProps.onChange(finalNum);
        } else {
          const event = new Event('input', { bubbles: true });
          input.dispatchEvent(event);
        }
      } catch (err) {
        // ignore
      }
    }
  };

  return (
    <StyledAmountGhostInput {...rest}>
      <span
        ref={amountGhostTextRef}
        className='amount-ghost-test'
      >
        {displayAmount}
      </span>
      <Form.Item name={name} {...formItemProps}>
        <Input
          ref={amountInputRef}
          prefix='€'
          type='text'
          inputMode='decimal'
          pattern='[0-9]*[.,]?[0-9]*'
          {...mergedInputProps}
        />
      </Form.Item>
    </StyledAmountGhostInput>
  );
}

AmountGhostInput.propTypes = {
  inputProps: PropTypes.object,
  formItemProps: PropTypes.object
}

export default AmountGhostInput;
